/*
 * BuddySIM destination search — shared matching logic.
 * Single source of truth used by the hero, the nav-bar overlay and the
 * Reiseziele collection grid so all three search the exact same way:
 * cities + EN/DE country aliases, diacritic-insensitive, with scoring.
 *
 * Public API (window.BuddySIMSearch):
 *   normalize(str)                       -> normalized string
 *   resolveAliasMatches(normalizedTerm)  -> { handleSuffix: true }
 *   itemMatches(rawQuery, name, handle)  -> bool  (grid filtering)
 *   loadDestinations(url)                -> Promise<indexed[]>
 *   findDestinations(rawQuery, products) -> scored, ranked array
 *   attach(opts)                         -> wire typeahead dropdown + jump
 */
(function (w) {
  'use strict';

  function destinationName(title) {
    return (title || '')
      .replace(/^eSIM[\s\-\u2013\u2014]+/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalize(value) {
    return destinationName(value)
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Semantic aliases: English/colloquial names + major cities → Shopify destination handle(s).
  var RAW_ALIASES = {
    // ── English / colloquial country names ──
    'usa': ['usa'], 'us': ['usa'], 'america': ['usa'], 'united states': ['usa'], 'united states of america': ['usa'],
    'uk': ['grossbritannien'], 'united kingdom': ['grossbritannien'], 'great britain': ['grossbritannien'], 'britain': ['grossbritannien'], 'england': ['grossbritannien'], 'scotland': ['grossbritannien'], 'schottland': ['grossbritannien'], 'wales': ['grossbritannien'],
    'uae': ['vereinigte-arabische-emirate'], 'emirates': ['vereinigte-arabische-emirate'],
    'korea': ['suedkorea'], 'south korea': ['suedkorea'],
    'germany': ['deutschland'], 'deutschland': ['deutschland'],
    'czech': ['tschechien'], 'czechia': ['tschechien'], 'czech republic': ['tschechien'],
    'holland': ['niederlande'], 'netherlands': ['niederlande'],
    'new zealand': ['neuseeland'], 'hong kong': ['hongkong'],
    'south africa': ['suedafrika'], 'saudi': ['saudi-arabien'], 'saudi arabia': ['saudi-arabien'],
    'dominican republic': ['dominikanische-republik'], 'turkey': ['tuerkei'], 'türkiye': ['tuerkei'], 'turkiye': ['tuerkei'],
    'greece': ['griechenland'], 'croatia': ['kroatien'], 'hungary': ['ungarn'], 'romania': ['rumaenien'],
    'egypt': ['aegypten'], 'brazil': ['brasilien'], 'mexico': ['mexiko'], 'india': ['indien'], 'russia': ['russland'],
    'denmark': ['daenemark'], 'sweden': ['schweden'], 'norway': ['norwegen'], 'finland': ['finnland'], 'ireland': ['irland'],
    'poland': ['polen'], 'belgium': ['belgien'], 'france': ['frankreich'], 'spain': ['spanien'], 'italy': ['italien'],
    'canada': ['kanada'], 'australia': ['australien'], 'singapore': ['singapur'], 'morocco': ['marokko'], 'tunisia': ['tunesien'],
    'colombia': ['kolumbien'], 'argentina': ['argentinien'], 'philippines': ['philippinen'], 'cambodia': ['kambodscha'],
    'jordan': ['jordanien'], 'bulgaria': ['bulgarien'], 'serbia': ['serbien'], 'albania': ['albanien'], 'georgia': ['georgien'],
    'armenia': ['armenien'], 'latvia': ['lettland'], 'lithuania': ['litauen'], 'estonia': ['estland'], 'slovenia': ['slowenien'],
    'slovakia': ['slowakei'], 'switzerland': ['schweiz'], 'austria': ['oesterreich'], 'indonesia': ['indonesien'],
    'maldives': ['malediven'], 'seychelles': ['seychellen'], 'iceland': ['island'], 'cyprus': ['zypern'], 'qatar': ['katar'],
    'israel': ['israel'], 'jamaica': ['jamaika'], 'cuba': ['kuba'], 'kazakhstan': ['kasachstan'], 'kyrgyzstan': ['kirgistan'],
    'tajikistan': ['tadschikistan'], 'azerbaijan': ['aserbaidschan'], 'moldova': ['moldawien'], 'macedonia': ['nordmazedonien'],
    'north macedonia': ['nordmazedonien'], 'bangladesh': ['bangladesch'], 'burma': ['myanmar'], 'kenya': ['kenia'],
    'tanzania': ['tansania'], 'zambia': ['sambia'], 'mozambique': ['mosambik'], 'madagascar': ['madagaskar'],
    'ethiopia': ['aethiopien'], 'ivory coast': ['elfenbeinkueste'], 'cameroon': ['kamerun'], 'rwanda': ['ruanda'],
    'greenland': ['groenland'], 'luxembourg': ['luxemburg'], 'vatican': ['vatikanstadt'], 'fiji': ['fidschi'],
    'algeria': ['algerien'], 'belarus': ['belarus'], 'trinidad': ['trinidad-und-tobago'], 'venezuela': ['lateinamerika'],
    // ── EN region names ──
    'africa': ['afrika'], 'asia': ['asien'], 'europe': ['europa'], 'oceania': ['ozeanien'],
    'north america': ['nordamerika'], 'south america': ['lateinamerika'], 'latin america': ['lateinamerika'],
    'caribbean': ['karibik'], 'middle east': ['naher-osten'], 'balkans': ['balkan'], 'canary islands': ['kanarische-inseln'],
    // ── German colloquial / Umgangssprache ──
    'amerika': ['usa'], 'vereinigte staaten': ['usa'], 'vereinigtes königreich': ['grossbritannien'],
    'dubai': ['vereinigte-arabische-emirate'], 'abu dhabi': ['vereinigte-arabische-emirate'], 'südkorea': ['suedkorea'],
    'kanaren': ['kanarische-inseln'], 'sansibar': ['tansania'], 'südsee': ['ozeanien'], 'naher osten': ['naher-osten'],
    // ── Major cities → country ──
    'tokio': ['japan'], 'tokyo': ['japan'], 'osaka': ['japan'], 'kyoto': ['japan'],
    'bangkok': ['thailand'], 'phuket': ['thailand'], 'chiang mai': ['thailand'], 'krabi': ['thailand'],
    'bali': ['indonesien'], 'jakarta': ['indonesien'],
    'new york': ['usa'], 'los angeles': ['usa'], 'san francisco': ['usa'], 'miami': ['usa'], 'las vegas': ['usa'], 'orlando': ['usa'], 'chicago': ['usa'], 'boston': ['usa'], 'washington': ['usa'],
    'london': ['grossbritannien'], 'manchester': ['grossbritannien'], 'edinburgh': ['grossbritannien'], 'liverpool': ['grossbritannien'],
    'paris': ['frankreich'], 'nizza': ['frankreich'], 'nice': ['frankreich'], 'marseille': ['frankreich'], 'lyon': ['frankreich'],
    'barcelona': ['spanien'], 'madrid': ['spanien'], 'mallorca': ['spanien'], 'ibiza': ['spanien'], 'teneriffa': ['spanien'], 'tenerife': ['spanien'], 'sevilla': ['spanien'], 'valencia': ['spanien'],
    'rom': ['italien'], 'rome': ['italien'], 'mailand': ['italien'], 'milan': ['italien'], 'venedig': ['italien'], 'venice': ['italien'], 'florenz': ['italien'], 'florence': ['italien'], 'neapel': ['italien'], 'naples': ['italien'], 'sardinien': ['italien'], 'sardinia': ['italien'], 'sizilien': ['italien'], 'sicily': ['italien'],
    'amsterdam': ['niederlande'], 'rotterdam': ['niederlande'],
    'lissabon': ['portugal'], 'lisbon': ['portugal'], 'porto': ['portugal'], 'algarve': ['portugal'],
    'istanbul': ['tuerkei'], 'antalya': ['tuerkei'], 'izmir': ['tuerkei'],
    'athen': ['griechenland'], 'athens': ['griechenland'], 'kreta': ['griechenland'], 'crete': ['griechenland'], 'santorini': ['griechenland'], 'mykonos': ['griechenland'], 'rhodos': ['griechenland'], 'korfu': ['griechenland'], 'corfu': ['griechenland'],
    'dubrovnik': ['kroatien'], 'split': ['kroatien'], 'zagreb': ['kroatien'],
    'prag': ['tschechien'], 'prague': ['tschechien'],
    'wien': ['oesterreich'], 'vienna': ['oesterreich'], 'salzburg': ['oesterreich'],
    'zürich': ['schweiz'], 'zurich': ['schweiz'], 'genf': ['schweiz'], 'geneva': ['schweiz'], 'basel': ['schweiz'],
    'berlin': ['deutschland'], 'münchen': ['deutschland'], 'munich': ['deutschland'], 'hamburg': ['deutschland'], 'frankfurt': ['deutschland'], 'köln': ['deutschland'], 'cologne': ['deutschland'], 'düsseldorf': ['deutschland'],
    'budapest': ['ungarn'], 'warschau': ['polen'], 'warsaw': ['polen'], 'krakau': ['polen'], 'krakow': ['polen'],
    'bukarest': ['rumaenien'], 'bucharest': ['rumaenien'], 'helsinki': ['finnland'], 'stockholm': ['schweden'], 'oslo': ['norwegen'],
    'kopenhagen': ['daenemark'], 'copenhagen': ['daenemark'], 'dublin': ['irland'], 'brüssel': ['belgien'], 'brussels': ['belgien'], 'antwerpen': ['belgien'],
    'toronto': ['kanada'], 'vancouver': ['kanada'], 'montreal': ['kanada'],
    'sydney': ['australien'], 'melbourne': ['australien'], 'brisbane': ['australien'], 'auckland': ['neuseeland'], 'wellington': ['neuseeland'],
    'seoul': ['suedkorea'], 'busan': ['suedkorea'], 'peking': ['china'], 'beijing': ['china'], 'shanghai': ['china'],
    'mumbai': ['indien'], 'delhi': ['indien'], 'new delhi': ['indien'], 'neu delhi': ['indien'],
    'kairo': ['aegypten'], 'cairo': ['aegypten'], 'hurghada': ['aegypten'], 'sharm el sheikh': ['aegypten'],
    'kapstadt': ['suedafrika'], 'cape town': ['suedafrika'], 'johannesburg': ['suedafrika'],
    'marrakesch': ['marokko'], 'marrakech': ['marokko'], 'casablanca': ['marokko'], 'agadir': ['marokko'],
    'cancun': ['mexiko'], 'mexiko city': ['mexiko'], 'mexico city': ['mexiko'], 'tulum': ['mexiko'], 'playa del carmen': ['mexiko'],
    'rio': ['brasilien'], 'rio de janeiro': ['brasilien'], 'sao paulo': ['brasilien'], 'são paulo': ['brasilien'],
    'buenos aires': ['argentinien'], 'bogota': ['kolumbien'], 'lima': ['peru'],
    'hanoi': ['vietnam'], 'ho chi minh': ['vietnam'], 'saigon': ['vietnam'], 'da nang': ['vietnam'],
    'kuala lumpur': ['malaysia'], 'manila': ['philippinen'], 'cebu': ['philippinen'], 'phnom penh': ['kambodscha'], 'siem reap': ['kambodscha'],
    'doha': ['katar'], 'muscat': ['oman'], 'tel aviv': ['israel'], 'jerusalem': ['israel'],
    'colombo': ['sri-lanka'], 'reykjavik': ['island'], 'valletta': ['malta'], 'nikosia': ['zypern'], 'nicosia': ['zypern'],
    'tbilisi': ['georgien'], 'baku': ['aserbaidschan'], 'almaty': ['kasachstan'], 'tashkent': ['usbekistan'],
    'nairobi': ['kenia'], 'zanzibar': ['tansania'], 'lagos': ['nigeria'], 'accra': ['ghana'], 'dakar': ['senegal']
  };

  // Build normalized lookup so query "zürich" → key "zuerich" etc.
  var ALIASES = {};
  Object.keys(RAW_ALIASES).forEach(function (raw) {
    var key = normalize(raw);
    if (!key) return;
    (ALIASES[key] = ALIASES[key] || []).push.apply(ALIASES[key], RAW_ALIASES[raw]);
  });

  function resolveAliasMatches(q) {
    var out = {};
    var qNo = q.replace(/[\s-]/g, '');
    for (var alias in ALIASES) {
      if (!ALIASES.hasOwnProperty(alias)) continue;
      var aNo = alias.replace(/[\s-]/g, '');
      var hit;
      if (aNo.length <= 3) {
        // Very short aliases (us, uk, uae, rom, rio) only match exact, to avoid over-firing.
        hit = (q === alias || qNo === aNo);
      } else {
        // Prefix / word-prefix matching: "tok"→tokyo, "new"→new york, but NOT "uk" inside "phuket".
        hit = (aNo.indexOf(qNo) === 0 || qNo.indexOf(aNo) === 0) ||
          alias.split(' ').some(function (word) { return word && word.indexOf(q) === 0; });
      }
      if (hit) ALIASES[alias].forEach(function (h) { out[h] = true; });
    }
    return out;
  }

  // Per-item match used by the Reiseziele grid filter (same logic, boolean result).
  function itemMatches(rawQuery, name, handle) {
    var term = normalize(rawQuery);
    if (!term) return true;
    var key = (handle || '').replace(/^esim-/, '');
    var text = normalize((name || '') + ' ' + key);
    var words = term.split(' ').filter(Boolean);
    var textMatch = words.every(function (word) { return text.indexOf(word) !== -1; });
    if (textMatch) return true;
    return resolveAliasMatches(term)[key] === true;
  }

  function indexProducts(items) {
    return (items || []).map(function (p) {
      p.key = (p.handle || '').replace(/^esim-/, '');
      p.searchText = normalize((p.name || p.title || '') + ' ' + p.key);
      return p;
    });
  }

  function findDestinations(rawQuery, products) {
    var term = normalize(rawQuery);
    if (!term) return [];
    var termNo = term.replace(/\s+/g, '');
    var words = term.split(' ').filter(Boolean);
    var aliasHandles = resolveAliasMatches(term);
    var exactAlias = {};
    if (ALIASES[term]) ALIASES[term].forEach(function (h) { exactAlias[h] = true; });

    var scored = [];
    (products || []).forEach(function (p) {
      var text = p.searchText;
      var textMatch = words.every(function (word) { return text.indexOf(word) !== -1; });
      var isAlias = aliasHandles[p.key] === true;
      if (!textMatch && !isAlias) return;
      var score;
      if (exactAlias[p.key]) score = 0;
      else if (p.key === termNo || text.split(' ')[0] === term) score = 1;
      else if (textMatch && (text.indexOf(term) === 0 || p.key.indexOf(termNo) === 0)) score = 2;
      else if (textMatch) score = 3;
      else score = 4;
      scored.push({ p: p, score: score });
    });
    scored.sort(function (a, b) { return a.score - b.score; });
    return scored.map(function (s) { return s.p; });
  }

  var _cache = {};
  function loadDestinations(url) {
    if (_cache[url]) return _cache[url];
    _cache[url] = fetch(url)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(indexProducts);
    return _cache[url];
  }

  // ── Dropdown rendering helpers ──
  function money(cents) {
    try { return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
    catch (e) { return (cents / 100).toFixed(2) + ' €'; }
  }

  function escapeHtml(value) {
    return (value || '').replace(/[&<>"']/g, function (char) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char];
    });
  }

  // Wire a typeahead dropdown with jump-to-destination on submit (hero + nav).
  // opts: { input, box, form, container, dataUrl, prefix, fallback }
  function attach(opts) {
    var input = opts.input, box = opts.box;
    if (!input || !box) return;
    var form = opts.form || input.form;
    var container = opts.container;
    var prefix = opts.prefix || 'bsds';
    var dataUrl = opts.dataUrl;
    var fallback = opts.fallback || '/collections/esim-reiseziele';
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    var rootClean = root.replace(/\/$/, '');
    var timer = null;

    function render(list, q) {
      if (!list || !list.length) {
        box.innerHTML = '<div class="' + prefix + '__sr-empty">Kein Reiseziel für „' + escapeHtml(q) +
          '" gefunden – <a href="' + rootClean + fallback + '">alle Reiseziele ansehen</a></div>';
        box.hidden = false;
        return;
      }
      box.innerHTML = list.slice(0, 6).map(function (p) {
        var img = p.image || '';
        var altText = p.alt || ('eSIM ' + (p.name || destinationName(p.title)));
        var price = (typeof p.price_min === 'number' && p.price_min > 0) ? money(p.price_min) : '';
        var thumb = img
          ? '<img class="' + prefix + '__sr-flag" src="' + escapeHtml(img) + '" alt="' + escapeHtml(altText) + '" width="28" height="28" loading="lazy">'
          : '<span class="' + prefix + '__sr-flag"></span>';
        return '<a class="' + prefix + '__sr-item" href="' + escapeHtml(p.url || '') + '">' + thumb +
          '<span class="' + prefix + '__sr-text"><span class="' + prefix + '__sr-name">' +
          escapeHtml(p.name || destinationName(p.title)) + '</span>' +
          (price ? '<span class="' + prefix + '__sr-price">eSIM ab ' + price + '</span>' : '') + '</span>' +
          '<svg class="' + prefix + '__sr-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg></a>';
      }).join('');
      box.hidden = false;
    }

    function search(q) {
      loadDestinations(dataUrl)
        .then(function (products) { render(findDestinations(q, products), q); })
        .catch(function () { box.hidden = true; });
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        var q = input.value.trim();
        if (!q) return;
        event.preventDefault();
        loadDestinations(dataUrl)
          .then(function (products) { return findDestinations(q, products); })
          .then(function (matches) {
            w.location.href = rootClean + ((matches && matches[0] && matches[0].url) || fallback);
          })
          .catch(function () { w.location.href = rootClean + fallback; });
      });
    }

    input.addEventListener('input', function () {
      var q = this.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { box.hidden = true; box.innerHTML = ''; return; }
      timer = setTimeout(function () { search(q); }, 180);
    });

    document.addEventListener('click', function (e) {
      if (container && !container.contains(e.target)) box.hidden = true;
    });

    input.addEventListener('focus', function () {
      if (box.innerHTML && this.value.trim().length >= 2) box.hidden = false;
    });
  }

  w.BuddySIMSearch = {
    normalize: normalize,
    resolveAliasMatches: resolveAliasMatches,
    itemMatches: itemMatches,
    loadDestinations: loadDestinations,
    findDestinations: findDestinations,
    attach: attach
  };
})(window);
